package com.swetdot.swetgram.data.push

import com.swetdot.swetgram.data.api.ApiClient
import com.swetdot.swetgram.data.api.ClientInfo
import com.swetdot.swetgram.protocol.dto.DevicePlatform
import com.swetdot.swetgram.protocol.dto.PushToken
import com.swetdot.swetgram.protocol.dto.RegisterPushTokenRequest
import io.ktor.client.request.setBody

/**
 * `POST /push-tokens` (operationId `registerPushToken`).
 *
 * Not idempotency-keyed, deliberately: the contract does not declare one for this operation,
 * and the server's own upsert on (user, device) makes a repeat harmless. A retry that created
 * a second registration would be the bug worth guarding against, and it cannot happen.
 */
class PushRegistrationRepository(
    private val api: ApiClient,
    private val clientInfo: ClientInfo,
) {
    /**
     * The platform comes from [ClientInfo], which every request already carries as
     * `X-Client-Platform` -- so the value the server sees in the header and the one it stores
     * on the row can never disagree. Passing it in from the caller would be a second source of
     * truth for the same fact.
     *
     * Returns Unit: the 201 body is a PushToken, but nothing on the client needs it, and not
     * decoding it keeps wire DTOs out of the platform entry points entirely.
     */
    suspend fun register(token: String, deviceId: String) {
        val platform = when (clientInfo.platform) {
            "ios" -> DevicePlatform.IOS
            else -> DevicePlatform.ANDROID
        }
        api.post<PushToken>("push-tokens") {
            setBody(RegisterPushTokenRequest(token = token, platform = platform, deviceId = deviceId))
        }
    }
}

/**
 * The platform-specific half of registration, implemented only where a push service exists.
 *
 * It is an interface rather than an `expect fun` because iOS has no implementation yet and an
 * unimplemented `actual` would fail the iOS build rather than simply doing nothing.
 */
fun interface PushRegistrar {
    /**
     * Fetches this installation's current token and registers it. Must swallow its own
     * failures: push not working is never a reason to keep a user out of the app.
     */
    suspend fun registerCurrentDevice()
}
